
import React, { useMemo, useState } from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import logo from "./zpa.png";
import api from "../../../api/api";

const BLACK = "#111111";
const DEEP_BLACK = "#080808";
const GOLD = "#D4AF37";
const LIGHT_GOLD = "#F3E4AD";
const IVORY = "#FFFDF7";
const WHITE = "#FFFFFF";
const GREY = "#666666";
const PALE_GOLD = "#F8F1DC";

const CARDS_PER_PAGE = 9;
const CARD_WIDTH = 252;
const CARD_HEIGHT = 158;

const styles = StyleSheet.create({
  page: {
    paddingTop: 19,
    paddingBottom: 18,
    paddingHorizontal: 28,
    backgroundColor: WHITE,
    fontFamily: "Helvetica",
  },

  pageHeading: {
    textAlign: "center",
    fontSize: 8,
    color: BLACK,
    marginBottom: 9,
    letterSpacing: 1.3,
    fontFamily: "Helvetica-Bold",
  },

  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignContent: "flex-start",
  },

  cardWrapper: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    marginBottom: 13,
    borderWidth: 1,
    borderColor: GOLD,
    borderRadius: 7,
    overflow: "hidden",
    backgroundColor: IVORY,
  },

  goldTopLine: {
    height: 3,
    backgroundColor: GOLD,
  },

  cardHeader: {
    height: 39,
    backgroundColor: BLACK,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    borderBottomWidth: 1.4,
    borderBottomColor: GOLD,
  },

  logoFrame: {
    width: 31,
    height: 31,
    backgroundColor: WHITE,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: GOLD,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 7,
    padding: 2,
  },

  logo: {
    width: 26,
    height: 26,
    objectFit: "contain",
  },

  schoolHeaderText: {
    flex: 1,
    justifyContent: "center",
  },

  schoolName: {
    fontSize: 9.1,
    fontFamily: "Helvetica-Bold",
    color: GOLD,
    lineHeight: 1.15,
  },

  schoolSystem: {
    fontSize: 6.1,
    color: WHITE,
    marginTop: 2,
    letterSpacing: 1,
  },

  schoolLevels: {
    fontSize: 4.8,
    color: LIGHT_GOLD,
    marginTop: 2,
  },

  cardTitle: {
    backgroundColor: PALE_GOLD,
    paddingVertical: 4,
    alignItems: "center",
    borderBottomWidth: 0.7,
    borderBottomColor: GOLD,
  },

  cardTitleText: {
    color: BLACK,
    fontSize: 7.8,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 1.4,
  },

  cardBody: {
    flexDirection: "row",
    paddingHorizontal: 9,
    paddingTop: 6,
    paddingBottom: 4,
    flex: 1,
  },

  photoColumn: {
    width: 62,
    alignItems: "center",
    marginRight: 9,
  },

  photoFrame: {
    width: 58,
    height: 66,
    padding: 2,
    backgroundColor: WHITE,
    borderWidth: 1.1,
    borderColor: GOLD,
    borderRadius: 4,
  },

  studentPhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: 2,
  },

  noPhoto: {
    width: 58,
    height: 66,
    borderWidth: 1,
    borderColor: GOLD,
    borderRadius: 4,
    backgroundColor: PALE_GOLD,
    alignItems: "center",
    justifyContent: "center",
    padding: 3,
  },

  noPhotoText: {
    fontSize: 6.5,
    color: GREY,
    textAlign: "center",
    fontFamily: "Helvetica-Bold",
  },

  photoCaption: {
    fontSize: 5.4,
    color: GREY,
    marginTop: 3,
    letterSpacing: 0.6,
  },

  detailsColumn: {
    flex: 1,
    justifyContent: "flex-start",
  },

  studentName: {
    color: BLACK,
    fontSize: 9.2,
    fontFamily: "Helvetica-Bold",
    marginBottom: 5,
    lineHeight: 1.15,
  },

  studentNameUnderline: {
    width: 33,
    height: 1.5,
    backgroundColor: GOLD,
    marginBottom: 4,
  },

  detailRow: {
    flexDirection: "row",
    marginBottom: 3,
    alignItems: "flex-start",
  },

  detailLabel: {
    width: 53,
    color: GREY,
    fontSize: 6.1,
  },

  detailValue: {
    flex: 1,
    color: BLACK,
    fontSize: 6.4,
    fontFamily: "Helvetica-Bold",
  },

  cardFooter: {
    height: 17,
    backgroundColor: BLACK,
    borderTopWidth: 1,
    borderTopColor: GOLD,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 5,
  },

  footerText: {
    color: LIGHT_GOLD,
    fontSize: 5.1,
    textAlign: "center",
    letterSpacing: 0.3,
  },

  backHeader: {
    height: 36,
    backgroundColor: BLACK,
    borderBottomWidth: 1.5,
    borderBottomColor: GOLD,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },

  backSchoolName: {
    color: GOLD,
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
  },

  backSchoolSystem: {
    color: WHITE,
    fontSize: 6,
    letterSpacing: 1,
    marginTop: 2,
  },

  backContent: {
    flex: 1,
    paddingHorizontal: 10,
    paddingTop: 7,
    paddingBottom: 4,
  },

  rulesTitle: {
    fontSize: 7.8,
    fontFamily: "Helvetica-Bold",
    color: BLACK,
    textAlign: "center",
    marginBottom: 5,
    letterSpacing: 0.6,
  },

  ruleRow: {
    flexDirection: "row",
    marginBottom: 3,
  },

  ruleNumber: {
    width: 13,
    color: "#A77B12",
    fontSize: 6.3,
    fontFamily: "Helvetica-Bold",
  },

  ruleText: {
    flex: 1,
    color: BLACK,
    fontSize: 6.1,
    lineHeight: 1.2,
  },

  addressText: {
    color: GREY,
    fontSize: 5.7,
    textAlign: "center",
    marginTop: 4,
    lineHeight: 1.2,
  },

  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 7,
    paddingHorizontal: 2,
  },

  signatureBlock: {
    width: "46%",
    alignItems: "center",
  },

  signatureLine: {
    width: "100%",
    borderTopWidth: 0.8,
    borderTopColor: BLACK,
    marginBottom: 3,
  },

  signatureLabel: {
    fontSize: 5.6,
    color: BLACK,
  },

  backFooter: {
    height: 17,
    backgroundColor: BLACK,
    borderTopWidth: 1,
    borderTopColor: GOLD,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 5,
  },

  backFooterText: {
    color: LIGHT_GOLD,
    fontSize: 5.2,
    textAlign: "center",
    letterSpacing: 0.2,
  },

  emptyCard: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    marginBottom: 13,
  },
});

const getValue = (student, keys, fallback = "N/A") => {
  for (const key of keys) {
    const value = student?.[key];

    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
    ) {
      return String(value);
    }
  }

  return fallback;
};

const getStudentName = (student) => {
  const fullName = getValue(
    student,
    ["student_name", "full_name", "name"],
    ""
  );

  if (fullName) return fullName;

  const firstName = getValue(student, ["first_name", "firstname"], "");
  const middleName = getValue(student, ["middle_name", "middlename"], "");
  const lastName = getValue(
    student,
    ["last_name", "lastname", "surname"],
    ""
  );

  return [firstName, middleName, lastName]
    .filter((part) => part && part !== "N/A")
    .join(" ") || "Student";
};

const getStudentImageUrl = (student) => {
  const imageFields = [
    "student_admission_image",
    "student_image",
    "profile_image",
    "photo",
    "image",
    "image_url",
  ];

  for (const field of imageFields) {
    const value = student?.[field];

    if (!value) continue;

    if (typeof value === "object") {
      const nestedUrl = value.url || value.image || value.src;
      if (nestedUrl) return nestedUrl;
      continue;
    }

    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return null;
};

const resolveImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== "string") return null;

  const trimmed = imageUrl.trim();

  if (
    /^https?:\/\//i.test(trimmed) ||
    /^data:image\//i.test(trimmed)
  ) {
    return trimmed;
  }

  try {
    const baseURL = api?.defaults?.baseURL;

    if (baseURL) {
      return new URL(trimmed, baseURL).href;
    }

    if (typeof window !== "undefined") {
      return new URL(trimmed, window.location.origin).href;
    }
  } catch (error) {
    console.error("Could not resolve student image URL:", error);
  }

  return null;
};

const getCardNumber = (student, index) =>
  getValue(
    student,
    [
      "card_number",
      "student_id",
      "admission_number",
      "registration_number",
    ],
    `ZPA-${String(index + 1).padStart(4, "0")}`
  );

const getGrade = (student) =>
  getValue(
    student,
    ["grade_class_name", "grade_class", "grade", "class_name", "class"],
    "N/A"
  );

const getDivision = (student) =>
  getValue(
    student,
    ["division_name", "division", "department"],
    "N/A"
  );

const getAcademicYear = (student) =>
  getValue(
    student,
    ["academic_year_name", "academic_year", "school_year"],
    "N/A"
  );

const getGender = (student) =>
  getValue(student, ["gender", "sex"], "N/A");

const getSchoolAddress = () =>
  "1st Comfield Junction, Duport Road, Paynesville, Monrovia, Liberia";

const chunkStudents = (students, size = CARDS_PER_PAGE) => {
  const chunks = [];

  for (let index = 0; index < students.length; index += size) {
    chunks.push(students.slice(index, index + size));
  }

  return chunks;
};

const StudentFrontCard = ({ student, index }) => {
  const imageUrl = resolveImageUrl(getStudentImageUrl(student));

  return (
    <View style={styles.cardWrapper}>
      <View style={styles.goldTopLine} />

      <View style={styles.cardHeader}>
        <View style={styles.logoFrame}>
          <Image src={logo} style={styles.logo} />
        </View>

        <View style={styles.schoolHeaderText}>
          <Text style={styles.schoolName}>
            ZION PRAISE ACADEMY
          </Text>

          <Text style={styles.schoolSystem}>
            CRM SCHOOL SYSTEM
          </Text>

          <Text style={styles.schoolLevels}>
            KINDERGARTEN • ELEMENTARY • JUNIOR & SENIOR HIGH
          </Text>
        </View>
      </View>

      <View style={styles.cardTitle}>
        <Text style={styles.cardTitleText}>
          OFFICIAL STUDENT IDENTIFICATION
        </Text>
      </View>

      <View style={styles.cardBody}>
        <View style={styles.photoColumn}>
          {imageUrl ? (
            <View style={styles.photoFrame}>
              <Image src={imageUrl} style={styles.studentPhoto} />
            </View>
          ) : (
            <View style={styles.noPhoto}>
              <Text style={styles.noPhotoText}>NO PHOTO</Text>
            </View>
          )}

          <Text style={styles.photoCaption}>STUDENT</Text>
        </View>

        <View style={styles.detailsColumn}>
          <Text style={styles.studentName}>
            {getStudentName(student)}
          </Text>

          <View style={styles.studentNameUnderline} />

          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>ID Number</Text>
            <Text style={styles.detailValue}>
              {getCardNumber(student, index)}
            </Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Grade/Class</Text>
            <Text style={styles.detailValue}>
              {getGrade(student)}
            </Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Division</Text>
            <Text style={styles.detailValue}>
              {getDivision(student)}
            </Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Gender</Text>
            <Text style={styles.detailValue}>
              {getGender(student)}
            </Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Academic Year</Text>
            <Text style={styles.detailValue}>
              {getAcademicYear(student)}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.cardFooter}>
        <Text style={styles.footerText}>
          EXCELLENCE • INTEGRITY • DISCIPLINE • ACADEMIC SUCCESS
        </Text>
      </View>
    </View>
  );
};

const StudentBackCard = () => (
  <View style={styles.cardWrapper}>
    <View style={styles.goldTopLine} />

    <View style={styles.backHeader}>
      <Text style={styles.backSchoolName}>
        ZION PRAISE ACADEMY
      </Text>

      <Text style={styles.backSchoolSystem}>
        CRM SCHOOL SYSTEM
      </Text>
    </View>

    <View style={styles.backContent}>
      <Text style={styles.rulesTitle}>
        STUDENT ID CARD REGULATIONS
      </Text>

      {[
        "This card is the property of Zion Praise Academy.",
        "Students should carry this card during school activities.",
        "Lost or damaged cards should be reported to the school office.",
        "This card is not transferable to another person.",
      ].map((rule, index) => (
        <View style={styles.ruleRow} key={rule}>
          <Text style={styles.ruleNumber}>{index + 1}.</Text>
          <Text style={styles.ruleText}>{rule}</Text>
        </View>
      ))}

      <Text style={styles.addressText}>
        {getSchoolAddress()}
      </Text>

      <View style={styles.signatureRow}>
        <View style={styles.signatureBlock}>
          <View style={styles.signatureLine} />
          <Text style={styles.signatureLabel}>
            Authorized School Official
          </Text>
        </View>

        <View style={styles.signatureBlock}>
          <View style={styles.signatureLine} />
          <Text style={styles.signatureLabel}>
            Parent / Guardian
          </Text>
        </View>
      </View>
    </View>

    <View style={styles.backFooter}>
      <Text style={styles.backFooterText}>
        IF FOUND, PLEASE RETURN TO ZION PRAISE ACADEMY
      </Text>
    </View>
  </View>
);

const makeNineSlots = (items) => {
  const slots = [...items];

  while (slots.length < CARDS_PER_PAGE) {
    slots.push(null);
  }

  return slots;
};

const StudentIDCardDocument = ({ students }) => {
  const studentPages = chunkStudents(students);

  return (
    <Document
      title="Zion Praise Academy Student ID Cards"
      author="Zion Praise Academy - CRM School System"
      subject="Official Student Identification Cards"
    >
      {studentPages.map((pageStudents, pageIndex) => {
        const slots = makeNineSlots(pageStudents);

        return (
          <React.Fragment key={`page-${pageIndex}`}>
            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
            >
              <Text style={styles.pageHeading}>
                ZION PRAISE ACADEMY • STUDENT ID CARDS • FRONT
              </Text>

              <View style={styles.grid}>
                {slots.map((student, slotIndex) =>
                  student ? (
                    <StudentFrontCard
                      key={`front-${pageIndex}-${slotIndex}`}
                      student={student}
                      index={pageIndex * CARDS_PER_PAGE + slotIndex}
                    />
                  ) : (
                    <View
                      key={`front-empty-${pageIndex}-${slotIndex}`}
                      style={styles.emptyCard}
                    />
                  )
                )}
              </View>
            </Page>

            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
            >
              <Text style={styles.pageHeading}>
                ZION PRAISE ACADEMY • STUDENT ID CARDS • BACK
              </Text>

              <View style={styles.grid}>
                {slots.map((student, slotIndex) =>
                  student ? (
                    <StudentBackCard
                      key={`back-${pageIndex}-${slotIndex}`}
                    />
                  ) : (
                    <View
                      key={`back-empty-${pageIndex}-${slotIndex}`}
                      style={styles.emptyCard}
                    />
                  )
                )}
              </View>
            </Page>
          </React.Fragment>
        );
      })}
    </Document>
  );
};

const StudentIDCardPDF = ({ students = [], fileName }) => {
  const [loading, setLoading] = useState(false);

  const validStudents = useMemo(
    () => (Array.isArray(students) ? students : []),
    [students]
  );

  const downloadPDF = async () => {
    if (!validStudents.length) {
      window.alert("No student records are available to generate ID cards.");
      return;
    }

    try {
      setLoading(true);

      const blob = await pdf(
        <StudentIDCardDocument students={validStudents} />
      ).toBlob();

      const safeFileName =
        fileName ||
        `Zion_Praise_Academy_Student_ID_Cards_${new Date()
          .toISOString()
          .slice(0, 10)}.pdf`;

      saveAs(blob, safeFileName);
    } catch (error) {
      console.error("Failed to generate student ID cards:", error);

      window.alert(
        "Unable to generate the student ID cards. Please check the student records and image URLs, then try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    downloadPDF,
    loading,
    students: validStudents,
  };
};

export {
  StudentIDCardDocument,
  StudentFrontCard,
  StudentBackCard,
  getStudentImageUrl,
  resolveImageUrl,
};

export default StudentIDCardPDF;
